/**
 * The one confirm before a push: which containers lose what, one row each
 * ("Default amount 802 SMS → 500 SMS · Verdicts"), and a short line for
 * what stays — the master's edits are saved first, a route test keeps its
 * own gateways, auto-send layers without the permission. Their own values
 * are dropped, so it reads as destructive.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenSection } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';

import { SECTION_LABEL } from '../../../campaign/master/rule-facts';
import { ContainerChip } from '../../../shared/ContainerChip';
import { givenBack, type MasterValues, type PushOwner } from './push-owners';

interface Props {
  open: boolean;
  /** One value's push; absent = every section. */
  section?: DropGenSection;
  owners: PushOwner[];
  masterName: string;
  values: MasterValues;
  notes: { savesFirst: boolean; routeTestsKeep: boolean; autoSendStays: boolean };
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const containers = (n: number) => `${n} container${n === 1 ? '' : 's'}`;

const Notes: FC<{ notes: Props['notes']; masterName: string; all: boolean }> = ({ notes, masterName, all }) => {
  const lines = [
    notes.savesFirst ? `Your edits to ${masterName} are saved first.` : null,
    all ? 'Schedules, slots and list groups stay each container’s own.' : null,
    notes.routeTestsKeep ? 'Route tests keep their own gateways — they are what they test.' : null,
    notes.autoSendStays ? 'Their own auto-send stays — taking it away needs the auto-send permission.' : null,
  ].filter(Boolean);
  if (lines.length === 0) return null;
  return (
    <ul className="flex flex-col gap-0.5 border-t border-border pt-2 text-xs text-muted-foreground">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
};

export const PushConfirmDialog: FC<Props> = ({ open, section, owners, masterName, values, notes, busy, onConfirm, onCancel }) => {
  const what = section ? `take ${masterName}’s ${SECTION_LABEL[section]}` : `follow ${masterName} on every value`;
  return (
    <ConfirmDialog
      open={open}
      title={section ? `Override ${SECTION_LABEL[section]} on ${containers(owners.length)}?` : `Override ${containers(owners.length)}?`}
      description={
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-muted-foreground">They {what}. Their own values are dropped.</p>
          <ul className="flex flex-col gap-2">
            {owners.map((o) => (
              <li key={o.container.id} className="flex min-w-0 items-start gap-3">
                <span className="flex shrink-0">
                  <ContainerChip name={o.container.name} color={o.container.color} routeTest={o.container.kind === 'ROUTE_TEST'} />
                </span>
                <span className="min-w-0 pt-0.5 text-xs text-muted-foreground">{givenBack(o, values).join(' · ')}</span>
              </li>
            ))}
          </ul>
          <Notes notes={notes} masterName={masterName} all={!section} />
        </div>
      }
      confirmLabel={`Override ${owners.length}`}
      confirmDisabled={owners.length === 0}
      destructive
      busy={busy}
      widthClass="max-w-lg"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
